/* @flow */

import React, { Component } from 'react';
import classNames from 'classnames';
import GeoSuggest from 'react-geosuggest';
import get from 'lodash/get';
import useSheet from 'app/jss';
import t from 'app/utils/translate';
import FormLabel from './FormLabel';

type Props = {
  apiKey: string,
  country?: string,
  disabled?: boolean,
  initialValue?: string,
  label?: string,
  lightContent?: boolean,
  name?: string,
  onChange: Object => void,
  required?: boolean,
  sheet: Object,
};

type State = {
  addressSelected: boolean,
  hasScriptLoaded: boolean,
};

class GeoAddressField extends Component<Props, State> {
  props: Props;

  state: State = {
    hasScriptLoaded: !!get(window, 'google.maps.Geocoder'),
  };

  static defaultProps = {
    country: undefined,
    disabled: false,
    initialValue: undefined,
    label: '',
    lightContent: false,
    name: '',
    required: false,
  };

  componentDidMount() {
    const { apiKey } = this.props;
    window.initMap = this.handleScriptLoad;
    const { hasScriptLoaded } = this.state;

    if (!hasScriptLoaded) {
      const scriptElem = document.createElement('script');
      const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initMap`;
      scriptElem.setAttribute('src', scriptUrl);
      // $FlowIgnore
      document.body.appendChild(scriptElem);
    }
  }

  ref = React.createRef();

  handleAddressSelected = (data) => {
    if (data) {
      const { onChange } = this.props;
      onChange(data.gmaps);
    }
  };

  handleScriptLoad = () => {
    this.setState({
      hasScriptLoaded: true,
    });
  };

  render() {
    const {
      country,
      disabled,
      initialValue,
      label,
      lightContent,
      name,
      required,
      sheet: { classes },
    } = this.props;
    const { hasScriptLoaded } = this.state;

    return (
      <div>
        {label && (
          <FormLabel htmlFor={name} lightContent={lightContent} required={required}>
            {label}
          </FormLabel>
        )}
        {hasScriptLoaded ? (
          <GeoSuggest
            className={classes.wrapper}
            country={country}
            disabled={disabled}
            initialValue={initialValue}
            inputClassName={classNames(classes.geoInput, { light: lightContent })}
            name={name}
            onSuggestSelect={this.handleAddressSelected}
            placeholder={t('Enter your address')}
            ref={this.ref}
          />
        ) : (
          <div>Loading...</div>
        )}
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    geoInput: {
      backgroundColor: theme.color.basis2,
      border: 0,
      borderRadius: theme.borderRadius.default,
      boxShadow: 'none',
      color: theme.color.basis6,
      fontSize: theme.fontSize.large,
      fontWeight: theme.fontWeight.demiBold,
      padding: theme.spacing.small,
      transition: 'background-color 0.2s, color 0.2s',
      '&::placeholder': {
        color: theme.color.basis3,
        '&.light': {
          color: theme.color.basis5,
        },
      },
      '&[disabled]': {
        color: theme.color.basis3,
      },
      '&:focus': {
        backgroundColor: theme.color.basis3,
        '&::placeholder': {
          color: theme.color.basis5,
        },
      },
      '&.light': {
        backgroundColor: theme.color.basis6,
        color: theme.color.basis1,
      },
      '&.light[disabled]': {
        backgroundColor: theme.color.basis6,
        color: theme.color.basis5,
      },
      '&.light:focus': {
        backgroundColor: theme.color.basis5,
      },
    },
    wrapper: {
      marginBottom: 20,
    },
  };
}

export default useSheet(applyStyles)(GeoAddressField);
